import { useSearchParams } from 'react-router-dom'
import { useTranslations } from 'use-intl'
import { PopupOutcomeCard, type PopupOutcomeCopy } from '@/components/PopupOutcomeCard'

const OUTCOMES = ['success', 'declined', 'cancelled', 'bank_error', 'error'] as const
type Outcome = (typeof OUTCOMES)[number]

// The consultant-side twin of apps/client's /bank/callback (FIN-709). Reached only via
// enable-banking-capture-redirect's server-side 302, which picks between the two apps from the
// initiating_app recorded when the connection started — never a real in-app navigation.
//
// Public on purpose: this renders in the one-off SCA popup Enable Banking redirects, which
// carries no session of its own. It also does no work — the connection itself completes in the
// original tab, which never navigated away and is still polling. So there is nothing here worth
// gating, and gating it would show a sign-in screen to someone who is already signed in next door.
//
// Any unrecognized outcome (a stale link, a typo) falls back to the generic "error" copy rather
// than rendering nothing or guessing at intent — same rule as the client page.
export default function BankCallback() {
  const [searchParams] = useSearchParams()
  const rawOutcome = searchParams.get('outcome')
  const outcome: Outcome = OUTCOMES.find((known) => known === rawOutcome) ?? 'error'
  const t = useTranslations('bank.callback')
  const copy: Record<Outcome, PopupOutcomeCopy> = {
    success: { title: t('success.title'), message: t('success.message'), variant: 'default' },
    declined: { title: t('declined.title'), message: t('declined.message'), variant: 'default' },
    cancelled: { title: t('cancelled.title'), message: t('cancelled.message'), variant: 'default' },
    bank_error: { title: t('bank_error.title'), message: t('bank_error.message'), variant: 'default' },
    error: { title: t('error.title'), message: t('error.message'), variant: 'destructive' },
  }

  return <PopupOutcomeCard copy={copy[outcome]} />
}
